import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GetClientsQueryParams } from '../dto/clients.dto';
import { BehaviorSubject, debounceTime, distinctUntilChanged, finalize, identity, Observable, switchMap, of, catchError } from 'rxjs';
import { GetProjectQueryParams, ProjetoDto } from '../dto/project.dto';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Projeto } from '../dto/project';
import { StatusProjeto } from '../dto/status-projeto';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private _apiUrl = environment.apiUrl + "/v1/Projetos";

  private filtroSubject = new BehaviorSubject<GetProjectQueryParams>({
    "PaginaAtual": 1,
    "QuantidadePorPagina": 10,
    Descricao: "",
  });

  private loadingSubject = new BehaviorSubject<boolean>(true);

  constructor(private http: HttpClient) { }

  get filters(): GetProjectQueryParams {
    return this.filtroSubject.value;
  }

  public get loading() {
    return this.loadingSubject.value;
  }

  setFilters(filters: Partial<GetProjectQueryParams>): void {
    const currentFilters = this.filtroSubject.value;
    // Preserve pagination settings when updating other filters
    this.filtroSubject.next({ ...currentFilters, ...filters });
  }

  getProjectByClienteId(clienteId: string): Observable<ProjetoDto> {

    return this.filtroSubject.pipe(
      debounceTime(400), // Debounce to avoid unnecessary requests
      distinctUntilChanged(), // Emit only when filters change
      switchMap((filters) => {
        let params = new HttpParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params = params.append(key, value.toString());
          }
        });

        this.loadingSubject.next(true); // Set loading state to true

        return this.http
          .get<ProjetoDto>(`${this._apiUrl}/${clienteId}`, { params })
          .pipe(
            finalize(() => this.loadingSubject.next(false)), // Set loading state to false after request
          );
      }),
    );
  }


  getProjectById(projetoId: string): Observable<Projeto> {
    return this.http.get<any>(`${this._apiUrl}/${projetoId}/projeto-id`).pipe(
      switchMap((response) => {
        // Mapear resposta da API para o formato Projeto esperado pelo componente
        const projeto: Projeto = {
          id: response.id,
          descricao: response.descricao,
          comprimento: response.comprimento,
          largura: response.largura,
          comprimentoSobrepor: response.comprimentoSobrepor,
          larguraSobrepor: response.larguraSobrepor,
          dataInicioFeira: response.dataInicioFeira,
          dataFimFeira: response.dataFimFeira,
          statusProjeto: {
            statusProjeto: response.statusAtualProjeto ?? 0
          } as any
        };
        return of(projeto);
      }),
      catchError((error) => {
        console.error('Erro ao buscar projeto:', error);
        throw error;
      })
    );
  }

  updateProject(projeto: Projeto): Observable<any> {
    // Montar payload conforme contrato da API: {id, descricao, statusProjeto, dataInicioFeira, dataFimFeira}
    const payload: any = {
      id: projeto.id,
      descricao: projeto.descricao,
      statusProjeto: projeto.statusProjeto?.statusProjeto ?? 0
    };

    // Adicionar comprimento e largura se existirem
    if ((projeto as any).comprimento !== undefined && (projeto as any).comprimento !== null) {
      payload.comprimento = (projeto as any).comprimento;
    }
    if ((projeto as any).largura !== undefined && (projeto as any).largura !== null) {
      payload.largura = (projeto as any).largura;
    }

    // Adicionar comprimentoSobrepor e larguraSobrepor se existirem
    if ((projeto as any).comprimentoSobrepor !== undefined && (projeto as any).comprimentoSobrepor !== null) {
      payload.comprimentoSobrepor = (projeto as any).comprimentoSobrepor;
    }
    if ((projeto as any).larguraSobrepor !== undefined && (projeto as any).larguraSobrepor !== null) {
      payload.larguraSobrepor = (projeto as any).larguraSobrepor;
    }

    // Adicionar datas se existirem (converter para ISO string se necessário)
    if (projeto.dataInicioFeira) {
      payload.dataInicioFeira = projeto.dataInicioFeira instanceof Date 
        ? projeto.dataInicioFeira.toISOString() 
        : new Date(projeto.dataInicioFeira).toISOString();
    }

    if (projeto.dataFimFeira) {
      payload.dataFimFeira = projeto.dataFimFeira instanceof Date 
        ? projeto.dataFimFeira.toISOString() 
        : new Date(projeto.dataFimFeira).toISOString();
    }

    return this.http.put(`${this._apiUrl}/${projeto.id}`, payload);
  }

  createproject(projeto: Projeto, clienteId?:string) {

    let paylaod: any = {
      clienteId: clienteId,
      descricao: projeto.descricao,
      feiraClienteId: projeto.feiraClienteId
    };

    // Adicionar comprimento e largura se existirem
    if ((projeto as any).comprimento !== undefined && (projeto as any).comprimento !== null) {
      paylaod.comprimento = (projeto as any).comprimento;
    }
    if ((projeto as any).largura !== undefined && (projeto as any).largura !== null) {
      paylaod.largura = (projeto as any).largura;
    }

    // Adicionar comprimentoSobrepor e larguraSobrepor se existirem
    if ((projeto as any).comprimentoSobrepor !== undefined && (projeto as any).comprimentoSobrepor !== null) {
      paylaod.comprimentoSobrepor = (projeto as any).comprimentoSobrepor;
    }
    if ((projeto as any).larguraSobrepor !== undefined && (projeto as any).larguraSobrepor !== null) {
      paylaod.larguraSobrepor = (projeto as any).larguraSobrepor;
    }

    // Adicionar datas da feira se existirem
    if ((projeto as any).dataInicioFeira !== undefined && (projeto as any).dataInicioFeira !== null) {
      paylaod.dataInicioFeira = (projeto as any).dataInicioFeira;
    }
    if ((projeto as any).dataFimFeira !== undefined && (projeto as any).dataFimFeira !== null) {
      paylaod.dataFimFeira = (projeto as any).dataFimFeira;
    }

    return this.http.post(this._apiUrl, paylaod);
  }

  excluirProjeto(projetoId?: string) : Observable<any>{
    return this.http.delete<any>(`${this._apiUrl}/${projetoId}`)
  }

  atualizarStatusProjeto(projetoId:string, status:number): Observable<string> {

    let paylaod = {
      projetoId: projetoId,
      statusProjeto: status
    }

    return this.http.patch<string>(`${this._apiUrl}/${projetoId}/status`, paylaod);
  }
}
